import {
    formatDate,
    getAddOns,
    getDemographicCatalog,
    getExperienceMin,
    getRestrictionErrorsForItem,
    getRestrictionErrorsKeyForItem,
    isEquipmentBased,
    isPast,
    ItemRestrictionErrors,
    now,
    omitEventRestrictionErrors,
    PurchaseItemStatus,
} from "common";
import dayjs, { Dayjs, min } from "dayjs";
import { produce } from "immer";
import { findLastIndex, isEmpty, sortBy, sumBy } from "lodash";
import {
    AllowedPrivacy,
    ApplyCode,
    ArrivalError,
    Card,
    CardInputMethod,
    Experience,
    ExperienceGroup,
    Form,
    FormResponse,
    GiftOffer,
    GratuityAlgorithm,
    Guest,
    GuestStatus,
    LineItem,
    LineItemCode,
    LineItemObject,
    LineItemTemplate,
    LineItemTemplateCode,
    LineItemType,
    Membership,
    Merchandise,
    MerchandiseFulfillmentStatus,
    Package,
    PackageType,
    PartnerObject,
    PartnerPackage,
    Payment,
    PaymentLineItemStatus,
    PaymentMethod,
    PaymentMethodCopy,
    PaymentReminder,
    PaymentReminderType,
    ProductCatalogItem,
    ProductObject,
    ProductReference,
    Purchase,
    PurchaseItemObject,
    PurchaseStatus,
    RewardCodeObject,
    Seller,
    SellerPaymentMethod,
    Tag,
    Timeslot,
    Traveler,
    UnitType,
} from "types";
import create from "zustand";
import { queryClient } from "../api/queryClient";
import { useAccountStore } from "./accountStore";
import { isWaitlistEnabled } from "./globalStore";
import { useCustomerDetailsStore } from "@/components/PurchaseCreate/store/customerDetailsStore";
import { usePurchaseResponseStore } from "@/components/PurchaseCreate/store/usePurchaseResponseStore";
import { getCatalogItemsByUnitType } from "@/models/Catalog";
import { getDefaultChoiceTemplateId, isMistPurchase } from "@/models/CreatePurchaseState";
import { isScheduled } from "@/models/Experience";
import { isPrimaryGuest } from "@/models/Guest";
import {
    filterPurchasableMerchandiseCatalogItems,
    getMerchandiseVariantMaxSelectableQuantity,
    hasCatalogItemPrice,
    isAutoFulfillment,
    isInventoryTracked,
} from "@/models/Merchandise";
import { isGift } from "@/models/Purchase";
import {
    isAllowedPrivacy,
    isExperiencePurchaseItem,
    isMembershipItem,
    isMerchandiseItem,
    sumLineItemQuantitiesByType,
} from "@/models/PurchaseItem";

export interface GiftOfferLineItem {
    code: string;
    amount?: number;
    id?: ID;
    type: LineItemType.GIFT;
    includeInAmount: boolean;
}

export interface CreatePurchaseItemState {
    id?: ID;
    quantity: number;
    amount?: number;
    arrivalDate: string;
    arrivalTime?: number;
    arrivalDateTime?: Dayjs;
    arrivalTimezoneName?: string;
    privacy: AllowedPrivacy;
    product: { id: ID };
    object: PurchaseItemObject;
    status: PurchaseItemStatus.DRAFT;
    lineItems: CreatePurchaseItemLineItem[];
    balanceDueOffset?: number;
    guestStatus?: GuestStatus;
    guests?: Guest[];
    timeslot?: Timeslot | null;
    parentProduct?: ProductReference;
    form?: Form;
    formResponse?: FormResponse;
    partner?: { id: ID; object: PartnerObject };
    seller?: { id: ID };
    source?: string;

    // Used in UI
    disableRequiredFormFields?: boolean;
    enableCustomLineItems?: boolean;
    paymentDueOffset?: number; // workaround to keep invoice field on switch from now to deposit

    customLineItems: Array<Partial<LineItem>>;
    demographicLineItems: DemographicLineItem[];
    equipmentLineItems?: EquipmentLineItem[];
    attendeeLineItems?: AttendeeLineItem[];
    wareLineItems?: WareLineItem[];
    fulfillmentStatus?: MerchandiseFulfillmentStatus;
    addonLineItems: AddonLineItem[];

    // Needed for gift
    isScannedGiftCard?: boolean;
    isRecipientSkipped?: boolean;
    code?: string;
    recipientName?: string;
    recipientEmail?: string;
    sendToRecipient?: boolean;
    message?: string;
    purchase?: { id: ID };
}

export interface CreatePurchaseLineItem {
    id?: ID;
    object: LineItemObject;
    type: LineItemType;
    amount?: number;
    appliedCode?: string; // `amount` and `code` are exclusive
    coupon?: { id: ID };
    payment?: Payment;
    card?: Card;
    status?: PaymentLineItemStatus;
}

export interface CreatePurchaseState {
    id?: ID;
    clientId?: ID;

    // Do not read this value in UI, use createCustomerDetailsStore
    customerName: string;
    customerEmail?: string;
    phone?: string;

    payNow?: number | null;
    amount: number;
    balance: number;
    dueNow: number;

    currency: string;
    items: CreatePurchaseItemState[];
    lineItems: CreatePurchaseLineItem[];
    paymentReminders?: PaymentReminder[];

    status: PurchaseStatus;
    splitPayment: { enabled: boolean };

    seller: { id: ID };
    partnerSeller?: { id: ID };
    partner?: { id: ID; object?: string };

    comment: string;
    notes?: [{ text: string }];
    tags?: Tag[];
    organizer?: { id: ID };
    deposit?: number;
}

export interface PaymentLineItem {
    id?: ID;
    object: LineItemObject.PAYMENT;
    type: LineItemType.PAYMENT;
    amount?: number;
    code?: string; // `amount` and `code` are exclusive
    payment: Payment;
    status?: PaymentLineItemStatus;
}

type CreatePurchaseProduct = Experience | GiftOffer | Package | Membership | PartnerPackage | Merchandise;

export interface GratuitySelection {
    amount: number;
    algorithm: GratuityAlgorithm;
    custom: boolean;
}

export interface DemographicLineItem {
    type: LineItemType.DEMOGRAPHIC;
    quantity: number;
    template: { id: ID };
    price?: number;
    amount?: number;
    code?: LineItemTemplateCode;
    id?: ID;
    name: string;
    sequence?: number;
    includeInAmount: boolean;
}

export interface AttendeeLineItem {
    type: LineItemType.DEMOGRAPHIC_SECONDARY;
    quantity: number;
    template: { id: ID };
    price?: number;
    amount?: number;
    id?: ID;
    name: string;
    sequence?: number;
    includeInAmount: boolean;
}

export interface EquipmentLineItem {
    type: LineItemType.EQUIPMENT;
    quantity: number;
    template: { id: ID };
    price?: number;
    id?: ID;
    name: string;
    sequence?: number;
    includeInAmount: boolean;
    open?: number;
}

export interface MembershipLineItem {
    type: LineItemType.MEMBERSHIP;
    id?: ID;
    template: { id?: ID };
    templateCode: LineItemCode.MEMBERSHIP_DISCOUNT;
    quantity: number;
    code: string;
    name: string;
    includeInAmount: boolean;
}

export interface AddonLineItem {
    type: LineItemType.ADDON | LineItemType.PICKUP;
    quantity: number;
    template: { id?: ID };
    name?: string;
    price?: number;
    amount?: number;
    id?: ID;
    includeInAmount: boolean;

    // Used in UI, sent as `_overrideRestrictions`
    overrideRestrictions?: boolean;
}

export interface CouponLineItem {
    type: LineItemType.COUPON_DISCOUNT;
    object: LineItemObject.COUPON;
    code: string;
    amount?: number;
    unlockAmount?: number;
    coupon: {
        id: ID;
        source?: string;
    };
    id?: ID;
    includeInAmount: boolean;
}

export interface GiftOfferLineItem {
    type: LineItemType.GIFT;
    code: string;
    amount?: number;
    id?: ID;
    includeInAmount: boolean;
}

export interface GratuityLineItem {
    id?: ID;
    type: LineItemType.GRATUITY;
    amount: number;
    name: string;
}

export interface CatalogLineItem {
    type: LineItemType.ADDON;
    quantity: number;
    template: { id: ID };
    catalog?: { template: ID };
    id?: ID;
    code?: string;
    amount?: number;
    includeInAmount: boolean;
    name?: string;
}

export interface WareLineItem {
    type: LineItemType.WARE;
    quantity: number;
    template: { id: ID };
    catalog?: { template: ID };
    id?: ID;
    code?: string;
    amount?: number;
    includeInAmount: boolean;
    name?: string;
}

export type CreatePurchaseItemLineItem =
    | DemographicLineItem
    | AttendeeLineItem
    | EquipmentLineItem
    | CouponLineItem
    | GiftOfferLineItem
    | AddonLineItem
    | MembershipLineItem
    | GratuityLineItem
    | WareLineItem;

type DraftType = (draft: CreatePurchaseState) => void;

interface GetPurchaseAndActiveItemResponse {
    purchase: CreatePurchaseState;
    item: CreatePurchaseItemState;
    activeItemIndex: number;
}

export interface CodeRestrictionModalState {
    isOpen: boolean;
    validationLevel: string;
    message: string;
    errorCode?: string;
    errorField?: string;
    codeType?: RewardCodeObject;
}

export interface CreatePurchaseStoreState {
    purchaseId: ID | null;
    isSubmitting: boolean;
    purchase: CreatePurchaseState;
    previousPurchase: Purchase;
    codeRequired: boolean;
    errors?: ArrivalError[];
    isPurchaserOrganizer: boolean;
    preventQuantitySync?: boolean;
    cardInputMethod?: CardInputMethod;
    organizer?: Traveler;
    activeItemIndex: number;
    code: ApplyCode | null;
    isFetchingPreviewPurchase: boolean;
    isEMV: boolean;
    autoCheckIn: boolean;
    eventId: ID | null;
    isAllProductDetailsUpdated: boolean;
    hasAvailableTimeslot?: boolean;
    waitlistId: ID | null;
    timeslot: Timeslot | null;
    paymentLineItem: PaymentLineItem;
    step: CreatePurchaseStep;
    balance: number;
    amount: number;
    dueNow: number;
    shouldIgnoreRestrictions: boolean;
    shouldShowCodeRestrictions: CodeRestrictionModalState;
    /** Keyed by item, since two items of a package or multi-item booking can carry the same add-on. */
    addOnRestrictionErrors: Record<string, ItemRestrictionErrors>;
    isWaitListEnabledPurchase?: boolean;
    isWaitListPurchase: boolean;
    isPartnerPackage: boolean | null;
    productObject?: ProductObject;
    purchaser?: ID;
    timeslotNotAvailable?: boolean;
    items: Array<{ index: number; arrivalDateTime?: Dayjs; id: ID }>;
    isFetchingPreview: boolean;
    addonLineItems: AddonLineItem[];
    demographicLineItems: DemographicLineItem[];
    wareLineItems: WareLineItem[];
    isProcessing?: boolean;
    rerunTrigger: number;
    itemUnitSubtotals: Array<{ id: ID; subtotal: number }>;
    itemGratuitySelections: Array<{
        id: ID;
        selection: GratuitySelection | null;
    }>;

    actions: {
        isInitialized: (experienceId: string) => boolean;
        initializeExperienceItem: (experience: Experience) => string | undefined;
        initializeMerchandiseItem: (merchandise: Merchandise) => string | undefined;
        setPurchase: (draftFunction: DraftType) => void;
        addItem: (item: CreatePurchaseItemState) => void;
        setActiveItemIndex: (activeItemIndex: number) => void;
        setIsFetchingPreviewPurchase: (isFetchingPreviewPurchase: boolean) => void;
        setIsEMV: (status: boolean) => void;
        setCurrency: (currency: string) => void;
        resetState: () => void;
        setPaymentStatus: (status: PaymentLineItemStatus) => void;
        setEventId: (id: ID | null) => void;
        setSeller: (id: ID) => void;
        setPartnerSeller: (id: ID) => void;
        setTimeslot: (id: Timeslot | null) => void;
        updateQuantity: (
            itemIndex: number,
            templateId: ID,
            newQuantity: number,
            targetDemographics?: LineItemTemplate,
        ) => void;
        updateWareQuantity: (itemIndex: number, templateId: ID, newQuantity: number) => void;
        updateEquipmentQuantity: (
            itemIndex: number,
            templateId: ID,
            newQuantity: number,
            name: string,
            sequence?: number,
        ) => void;
        updateAttendeeQuantity: (
            itemIndex: number,
            templateId: ID,
            newQuantity: number,
            name: string,
            sequence?: number,
        ) => void;
        updateGuests: (itemIndex: number, guests: Guest[]) => void;
        setWaitlistId: (id: ID | null) => void;
        setPayment: (payment: Payment) => void;
        setPaymentMethod: (method: PaymentMethod) => void;
        setActiveItemGratuitySelection: (amount: number, algorithm: GratuityAlgorithm, custom: boolean) => void;
        initializePaymentLineItem: (amount: number, method: PaymentMethod) => void;
    };

    getForm: () => Form | undefined;
    getGuests: () => Guest[];
    showAddCode: (index: number) => boolean;
    showPrivacyWarning: () => boolean;
    isMist: () => boolean;
    isPartnerPurchase: () => boolean;
    hasDeposit: () => boolean;
    hasExperienceItems: () => boolean;
    isOneStepCheckout: () => boolean;
    getPurchasePackageId: () => ID | null;
    isVariableDemographicsPackage: () => boolean;
    getProductsInPurchase: () => Experience[];
    getItemExperienceByIndex: (index: number) => Experience;
    getPrimaryGuest: () => Guest | undefined;
    getProductGroup: () => ExperienceGroup | undefined;
    isWaitListEnabledForPurchase: () => boolean;
    isWaitListMode: () => boolean;
    hasTimeslot: () => boolean;
    getArrivalDateTime: () => Dayjs | undefined;
    getWareLineItems: () => WareLineItem[];
    getAddonLineItems: () => AddonLineItem[];
    getDemographicLineItems: () => DemographicLineItem[];
    getEquipmentLineItems: () => EquipmentLineItem[];
    getAttendeeLineItems: () => AttendeeLineItem[];
    getActiveItem: () => CreatePurchaseItemState;
    getFirstItem: () => CreatePurchaseItemState;
    getPurchaseAndActiveItem: () => GetPurchaseAndActiveItemResponse;
    getActiveProduct: <T extends CreatePurchaseProduct = CreatePurchaseProduct>() => T;
    getActiveItemProductId: () => ID;
    getSeller: () => { id: ID };
    getEarliestArrivalDateTime: () => Dayjs;
    getItemUnitSubtotal: (id: ID) => number;
    getQuantity: (itemId?: ID) => number;
    getItemGratuitySelection: (id: ID) => GratuitySelection | null;
    isPastDateBooking(): boolean;
    /** Whether this item's arrival has passed. The API skips limits per item. */
    isPastDatedItem(index: number): boolean;
    getAddOnRestrictionErrorsKey(index: number): string;
    getAddOnRestrictionErrors(index: number): ItemRestrictionErrors | undefined;
}

export const getDefaultPaymentMethod = (seller: Seller): SellerPaymentMethod => {
    const favouriteMethod = seller.paymentMethods?.find((method) => method.favourite);

    return (
        favouriteMethod ?? {
            id: PaymentMethod.CREDIT_CARD,
            name: getPaymentMethodName(seller, PaymentMethod.CREDIT_CARD)!,
        }
    );
};

export const getPaymentMethodName = (seller: Seller, method: PaymentMethod | ID) => {
    const realMethod: PaymentMethod = method as PaymentMethod; // For preventing lint errors

    if (PaymentMethodCopy[realMethod]) {
        return PaymentMethodCopy[realMethod];
    }

    return seller.paymentMethods?.find(({ id }) => id === method)?.name;
};

const defaultPurchaseState: CreatePurchaseState = {
    clientId: undefined,
    customerName: "",
    amount: 0,
    balance: 0,
    dueNow: 0,
    currency: "USD",
    items: [],
    lineItems: [],
    tags: [],
    comment: "",
    splitPayment: { enabled: false },
    seller: { id: "" },
    status: PurchaseStatus.DRAFT,
};

const defaultState: Partial<CreatePurchaseStoreState> = {
    purchaseId: null,
    isSubmitting: false,
    purchase: defaultPurchaseState,
    activeItemIndex: 0,
    isFetchingPreviewPurchase: false,
    codeRequired: false,
    preventQuantitySync: false,
    isEMV: false,
    autoCheckIn: false,
    code: null,
    eventId: null,
    timeslot: null,
    isAllProductDetailsUpdated: false,
    isPurchaserOrganizer: true,
    cardInputMethod: undefined,
    hasAvailableTimeslot: undefined,
    paymentLineItem: {
        object: LineItemObject.PAYMENT,
        type: LineItemType.PAYMENT,
        payment: { method: PaymentMethod.CREDIT_CARD },
        status: PaymentLineItemStatus.PROCESSING,
    },
    shouldIgnoreRestrictions: false,
    addOnRestrictionErrors: {},
    shouldShowCodeRestrictions: {
        isOpen: false,
        validationLevel: "",
        message: "",
    },
    organizer: undefined,
    isWaitListEnabledPurchase: undefined,
    isWaitListPurchase: false,
    isPartnerPackage: null,
    items: [],
    isFetchingPreview: false,
    isProcessing: undefined,
    rerunTrigger: 0,
    itemUnitSubtotals: [],
    itemGratuitySelections: [],
};

export const useCreatePurchaseStore = create<CreatePurchaseStoreState>((set, get) => ({
    ...(defaultState as CreatePurchaseStoreState),

    getActiveItem() {
        const currentIndex = get().activeItemIndex;
        return get().purchase.items[currentIndex];
    },

    getForm() {
        const { getters } = usePurchaseResponseStore.getState();
        const itemForm = getters.getForm(get().activeItemIndex);

        return itemForm ?? getters.getPackageForm() ?? undefined;
    },

    getFirstItem() {
        return get().purchase.items[0];
    },

    getAmount() {
        return get().amount;
    },

    getBalance() {
        return get().balance;
    },

    getItemUnitSubtotal(id: ID) {
        return get().itemUnitSubtotals.find((itemSubtotal) => itemSubtotal.id === id)?.subtotal ?? 0;
    },

    getQuantity(itemId?: ID) {
        if (!itemId) {
            return 0;
        }

        return get().purchase.items.find((item) => item.id === itemId)?.quantity ?? 0;
    },

    getItemGratuitySelection(id: ID) {
        return get().itemGratuitySelections.find((itemSelection) => itemSelection.id === id)?.selection ?? null;
    },

    getPurchaseAndActiveItem() {
        return {
            purchase: get().purchase,
            item: get().getActiveItem(),
            activeItemIndex: get().activeItemIndex,
        };
    },

    getActiveProduct() {
        const item = get().getActiveItem();
        return getItemProduct(item);
    },

    getItemExperienceByIndex(index) {
        const item = get().purchase.items[index];
        return getItemProduct(item);
    },

    getSeller() {
        return get().purchase.seller;
    },

    getEarliestArrivalDateTime() {
        const items = get().purchase.items;
        const nonMerchItems = items.filter((item) => !isMerchandiseItem(item));

        if (nonMerchItems.length === 0) {
            return now().startOf("day");
        }

        return min(nonMerchItems.map((item) => now(item.arrivalDate) ?? now().startOf("day")))!;
    },

    hasDeposit() {
        return (
            get().purchase.paymentReminders?.some(({ type }) => type === PaymentReminderType.SELLER_DEPOSIT) ?? false
        );
    },

    hasExperienceItems() {
        return get().purchase.items.some((item) => isExperiencePurchaseItem(item));
    },

    isMist() {
        return get().purchase.items.length > 1;
    },

    isPartnerPurchase() {
        const purchase = get().purchase;
        const hasPartnerLineItems = purchase.lineItems.some((lineItem) =>
            [LineItemType.PARTNER_PURCHASE_ITEM, LineItemType.PARTNER_PACKAGE_ITEM].includes(lineItem.type),
        );
        const hasPartnerItems = purchase.items.some((item) => item.seller && item.seller.id !== purchase.seller.id);
        return hasPartnerLineItems || hasPartnerItems;
    },

    isNowPaymentWorkflow() {
        if (
            // Payment Workflow: Later
            get().paymentLineItem.payment.method === PaymentMethod.LATER ||
            // Payment Workflow: Deposit
            get().purchase.paymentReminders?.length ||
            // Payment Workflow: Split Payment
            get().purchase.splitPayment.enabled
        ) {
            return false;
        }

        return true;
    },

    getActiveItemProductId() {
        return get().getActiveItem()?.parentProduct?.id ?? get().getActiveItem()?.product.id;
    },

    isOneStepCheckout() {
        return !get().isMist();
    },

    isVariableDemographicsPackage() {
        const item = get().getActiveItem();
        // We will always have the package product in the cache before this is called
        const packageDetails = queryClient.getQueryData<Package | PartnerPackage>([
            "products",
            item?.parentProduct?.id,
        ])!;
        return packageDetails ? packageDetails.type === PackageType.DEMOGRAPHICS_VARIES : false;
    },

    getPurchasePackageId() {
        const packageId = get().purchase.items?.[0]?.parentProduct?.id;
        return packageId ?? null;
    },

    getProductsInPurchase() {
        return get().purchase.items.map((item) => {
            return getItemProduct<Experience>(item);
        });
    },

    getPrimaryGuest() {
        const item = get().getActiveItem();
        const { purchase } = get();
        return item.guests?.find((guest) => isPrimaryGuest(guest, purchase, item));
    },

    isWaitListMode() {
        const isWaitlistEnabled = get().isWaitListEnabledPurchase;
        const activeItem = get().getActiveItem();
        const { quantity, timeslot } = activeItem;
        const { outingMax = Number.MAX_VALUE } = get().getActiveProduct<Experience>()?.group ?? {};

        const isWaitListMode =
            (isWaitlistEnabled && !!timeslot && quantity > timeslot.capacity.open && quantity <= outingMax) ?? false;

        return isWaitListMode;
    },

    hasTimeslot() {
        return !!get().timeslot;
    },

    getProductGroup() {
        return get().getActiveProduct<Experience>()?.group;
    },

    getArrivalDateTime() {
        return (get()?.items?.length ?? 0) > 0 ? get().items[get().activeItemIndex]?.arrivalDateTime : undefined;
    },

    getAddonLineItems() {
        return get().getActiveItem()?.addonLineItems ?? [];
    },

    getWareLineItems() {
        return get().getActiveItem()?.wareLineItems ?? [];
    },

    getDemographicLineItems() {
        return get().getActiveItem()?.demographicLineItems ?? [];
    },

    getEquipmentLineItems() {
        return get().getActiveItem()?.equipmentLineItems ?? [];
    },

    getAttendeeLineItems() {
        return get().getActiveItem()?.attendeeLineItems ?? [];
    },

    isPastDateBooking() {
        return get().getEarliestArrivalDateTime().isBefore(now(), "day");
    },

    isPastDatedItem(index) {
        const arrivalDate = get().purchase.items[index]?.arrivalDate;
        return Boolean(arrivalDate) && isPast(arrivalDate);
    },

    getAddOnRestrictionErrorsKey(index) {
        const { addOnRestrictionErrors, purchase } = get();
        return getRestrictionErrorsKeyForItem(addOnRestrictionErrors, purchase.items[index]?.id, index);
    },

    getAddOnRestrictionErrors(index) {
        const { addOnRestrictionErrors, purchase } = get();
        return getRestrictionErrorsForItem(addOnRestrictionErrors, purchase.items[index]?.id, index);
    },

    showAddCode(index) {
        const { purchase } = usePurchaseResponseStore.getState();
        const showPayments = !get().isWaitListMode();
        const activeItem = purchase.items?.[index];

        if (!activeItem) {
            return false;
        }

        const isEligibleItem =
            isExperiencePurchaseItem(activeItem) || isMembershipItem(activeItem) || isMerchandiseItem(activeItem);
        return showPayments && isEligibleItem;
    },

    showPrivacyWarning() {
        const experiences = get().getProductsInPurchase();
        const { purchase } = usePurchaseResponseStore.getState();
        const productIds = purchase.items?.map((item) => item.product.id);
        const purchaseItemsProductIds = new Set(productIds);
        const purchasedExperiences = experiences.filter((experience) => purchaseItemsProductIds.has(experience.id));
        const shouldShowPrivacyWarning = purchasedExperiences.some((experience) => {
            const purchasedItem = purchase.items?.find((item) => item.product.id === experience.id);
            return (
                experience &&
                purchasedItem &&
                isAllowedPrivacy(purchasedItem?.privacy) &&
                !experience.allowedPrivacies.includes(purchasedItem?.privacy)
            );
        });

        return shouldShowPrivacyWarning;
    },

    actions: {
        isInitialized: (experienceId: string) => {
            const hasProductId = get().purchase.items.some((item) => item.product.id === experienceId);
            return !!get().purchase.seller.id && hasProductId;
        },

        initializeExperienceItem: (experience) => {
            get().actions.setSeller(experience.seller.id);

            const defaultArrival = { date: formatDate(dayjs().tz()), time: null };
            const privacy =
                experience.allowedPrivacies.length === 1 ? experience.allowedPrivacies[0] : AllowedPrivacy.PUBLIC;

            const item: CreatePurchaseItemState = {
                arrivalDate: defaultArrival.date,
                arrivalTime: defaultArrival.time ?? undefined,
                privacy: privacy,
                product: { id: experience.id },
                quantity: getExperienceMin(experience),
                demographicLineItems: isEquipmentBased(experience)
                    ? []
                    : getExperienceDemographicLineItems(
                          getDemographicCatalog(experience),
                          getExperienceMin(experience),
                      ),
                equipmentLineItems: isEquipmentBased(experience)
                    ? getExperienceEquipmentLineItems(experience)
                    : undefined,
                attendeeLineItems: isEquipmentBased(experience)
                    ? getExperienceAttendeeLineItems(experience)
                    : undefined,
                addonLineItems: getExperienceAddonLineItems(experience),
                lineItems: [],
                seller: { id: experience.seller.id },
                balanceDueOffset: experience.balanceDueOffset ? experience.balanceDueOffset / 1440 : 0,
                guests: [],
                status: PurchaseItemStatus.DRAFT,
                object: isEquipmentBased(experience)
                    ? PurchaseItemObject.EQUIPMENT
                    : isScheduled(experience)
                    ? PurchaseItemObject.EXPERIENCE
                    : PurchaseItemObject.ORDER,
                customLineItems: [],
            };

            get().actions.addItem(item);

            return item.id;
        },

        initializeMerchandiseItem: (merchandise) => {
            get().actions.setSeller(merchandise.seller.id);

            const wareLineItems = getMerchandisePurchaseWareLineItems(merchandise);
            const isAutoFulfill = isAutoFulfillment(merchandise);

            const item: CreatePurchaseItemState = {
                arrivalDate: formatDate(dayjs().tz()),
                privacy: AllowedPrivacy.PUBLIC,
                product: { id: merchandise.id },
                quantity: sumBy(wareLineItems, "quantity"),
                demographicLineItems: [],
                addonLineItems: [],
                wareLineItems,
                lineItems: [],
                status: PurchaseItemStatus.DRAFT,
                object: PurchaseItemObject.WARE,
                customLineItems: [],
                fulfillmentStatus: isAutoFulfill
                    ? MerchandiseFulfillmentStatus.PROCESSING_FULFILLMENT
                    : MerchandiseFulfillmentStatus.PENDING_FULFILLMENT,
            };

            get().actions.addItem(item);

            return item.id;
        },

        setPurchase(draftFunction) {
            set((state) => {
                const purchase = produce(state.purchase, draftFunction);
                const addOnRestrictionErrors = omitEventRestrictionErrorsForMovedItems(
                    state.addOnRestrictionErrors,
                    state.purchase.items,
                    purchase.items,
                );

                return { purchase, addOnRestrictionErrors };
            });
        },

        addItem(item) {
            set((state) =>
                produce(state, (draft) => {
                    const length = draft.purchase.items.push(item);
                    draft.activeItemIndex = length - 1;

                    const isGiftOrMembership = isGift(item) || isMembershipItem(item);

                    draft.isPurchaserOrganizer =
                        isMistPurchase(draft.purchase) || isGiftOrMembership ? false : state.isPurchaserOrganizer;

                    if (isGiftOrMembership && !isNowPaymentWorkflowPurchase(draft.paymentLineItem, draft.purchase)) {
                        /** When user adds a gift or membership purchase item and currently selected payment workflow is not "NOW", we need to set payment workflow to "NOW" */
                        draft.paymentLineItem.payment.method = PaymentMethod.CREDIT_CARD;
                        draft.purchase.paymentReminders = []; // No longer a deposit
                        draft.purchase.splitPayment.enabled = false;
                        delete draft.purchase.payNow;
                    }

                    const experience = getItemProduct<Experience>(item);

                    draft.isWaitListEnabledPurchase =
                        experience.waitlist?.enabled && !get().isMist() && isWaitlistEnabled();
                }),
            );
        },

        setActiveItemIndex(activeItemIndex: number) {
            set(() => ({ activeItemIndex }));
        },

        setIsFetchingPreviewPurchase(isFetchingPreviewPurchase: boolean) {
            set(() => ({ isFetchingPreviewPurchase }));
        },

        setPaymentStatus(paymentStatus: PaymentLineItemStatus) {
            set((state) => {
                return produce(state, (draft) => {
                    draft.paymentLineItem.status = paymentStatus;
                });
            });
        },

        setIsEMV(isEMV) {
            set(() => ({ isEMV }));
        },

        setCurrency(currency) {
            set((state) => {
                return produce(state, (draft) => {
                    draft.purchase.currency = currency;
                });
            });
        },

        setSeller(id: ID) {
            set((state) => {
                return produce(state, (draft) => {
                    draft.purchase.seller.id = id;
                });
            });
        },

        setPartnerSeller(id: ID) {
            set((state) => {
                return produce(state, (draft) => {
                    draft.purchase.partnerSeller = { id: id };
                });
            });
        },

        setEventId(eventId) {
            set(() => ({ eventId }));
        },

        setWaitlistId(waitlistId) {
            set(() => ({ waitlistId }));
        },

        setActiveItemGratuitySelection(amount: number, algorithm: GratuityAlgorithm, custom = false) {
            const activeItem = get().getActiveItem();
            if (!activeItem) return;

            set((state) => {
                return produce(state, (draft) => {
                    const existingSelectionIndex = draft.itemGratuitySelections.findIndex(
                        (item) => item.id === activeItem.id,
                    );

                    if (existingSelectionIndex >= 0) {
                        const existing = draft.itemGratuitySelections[existingSelectionIndex].selection;
                        if (
                            existing?.amount !== amount ||
                            existing.algorithm !== algorithm ||
                            existing.custom !== custom
                        ) {
                            draft.itemGratuitySelections[existingSelectionIndex].selection = {
                                amount,
                                algorithm,
                                custom,
                            };
                        }
                    } else {
                        draft.itemGratuitySelections.push({
                            id: activeItem.id!,
                            selection: { amount, algorithm, custom },
                        });
                    }
                });
            });
        },

        resetState() {
            const sellerId = useAccountStore.getState().seller;
            const seller = queryClient.getQueryData<Seller>(["sellers", sellerId]);
            const newState: Partial<CreatePurchaseStoreState> = { ...structuredClone(defaultState) };

            if (newState?.paymentLineItem?.payment.method && seller) {
                const defaultMethod = getDefaultPaymentMethod(seller).id;
                newState.paymentLineItem = {
                    object: LineItemObject.PAYMENT,
                    type: LineItemType.PAYMENT,
                    payment: { method: defaultMethod },
                    status: PaymentLineItemStatus.PROCESSING,
                };
            }

            if (seller && newState.purchase) {
                newState.purchase.currency = seller.currency;
                newState.purchase.seller = { id: seller.id };
            }

            set(newState as CreatePurchaseStoreState);
            clearDraftPurchasesCache();
            useCustomerDetailsStore.getState().actions.resetState();
            usePurchaseResponseStore.getState().actions.resetState();
        },

        setTimeslot(timeslot) {
            set(() => ({ timeslot }));
        },

        updateQuantity(itemIndex: number, templateId: ID, newQuantity: number, targetDemographics?: LineItemTemplate) {
            set((state) => ({
                purchase: produce(state.purchase, (draft) => {
                    if (state.getPurchasePackageId() && !state.isVariableDemographicsPackage()) {
                        // For a package purchase, update quantity for all items
                        for (const [index, item] of draft.items.entries()) {
                            const demographicLineItem = item.demographicLineItems.find((lineItem) => {
                                return "template" in lineItem && lineItem.template.id === templateId;
                            }) as DemographicLineItem;

                            const lineItemIndex = item.demographicLineItems.indexOf(demographicLineItem);
                            demographicLineItem.quantity = newQuantity;
                            draft.items[index].demographicLineItems = Object.assign([], item.demographicLineItems, {
                                [lineItemIndex]: demographicLineItem,
                            });

                            draft.items[index].quantity = sumBy(draft.items[index].demographicLineItems, "quantity");
                        }
                    } else {
                        const activeItem = draft.items[itemIndex];
                        const isMembership = activeItem.object === PurchaseItemObject.MEMBERSHIP;
                        const lineItems = isMembership ? activeItem.lineItems : activeItem.demographicLineItems;

                        const demographicLineItem = lineItems.find(
                            (lineItem) => "template" in lineItem && lineItem.template.id === templateId,
                        ) as DemographicLineItem;

                        if (demographicLineItem) {
                            // workaround (https://xola01.atlassian.net/browse/X2-5063?focusedCommentId=101313)
                            const oldQuantity = demographicLineItem.quantity;
                            const guestsDraft = activeItem.guests;
                            const isDecreaseQuantity = oldQuantity > newQuantity;

                            if (
                                isDecreaseQuantity &&
                                guestsDraft &&
                                targetDemographics &&
                                /** XS-67: When a user changes the guest quantity with "Remove Guest modal", the data within guests draft may have already been removed with `store.updateGuests` call. if so, we don't want to remove any guest data */
                                guestsDraft.length !== newQuantity
                            ) {
                                const indexToRemove = findLastIndex(
                                    guestsDraft,
                                    (guest) => guest?.unit?.id === targetDemographics.unit?.id,
                                );
                                if (indexToRemove >= 0) {
                                    guestsDraft.splice(indexToRemove, 1);
                                }
                            }

                            demographicLineItem.quantity = newQuantity;
                        }

                        // set item qty to sum of all demographic/membership quantities
                        activeItem.quantity = sumLineItemQuantitiesByType(
                            lineItems,
                            isMembership ? LineItemType.MEMBERSHIP : LineItemType.DEMOGRAPHIC,
                        );
                    }
                }),
            }));
        },

        updateWareQuantity(itemIndex: number, templateId: ID, newQuantity: number) {
            set((state) => ({
                purchase: produce(state.purchase, (draft) => {
                    const activeItem = draft.items[itemIndex];
                    const existingWareLineItems = activeItem.wareLineItems ?? [];
                    const lineItemIndex = existingWareLineItems.findIndex(
                        (lineItem) => lineItem.template.id === templateId,
                    );

                    const nextWareLineItems = [...existingWareLineItems];

                    if (lineItemIndex >= 0) {
                        const currentLineItem = nextWareLineItems[lineItemIndex];
                        nextWareLineItems[lineItemIndex] = {
                            ...currentLineItem,
                            quantity: newQuantity,
                        };
                    } else if (newQuantity > 0) {
                        nextWareLineItems.push({
                            type: LineItemType.WARE,
                            quantity: newQuantity,
                            template: { id: templateId },
                            includeInAmount: true,
                        });
                    }

                    activeItem.wareLineItems = nextWareLineItems.filter((lineItem) => lineItem.quantity > 0);

                    activeItem.quantity = sumBy(activeItem.wareLineItems, "quantity");
                }),
            }));
        },

        updateEquipmentQuantity(
            itemIndex: number,
            templateId: ID,
            newQuantity: number,
            name: string,
            sequence?: number,
        ) {
            set((state) => ({
                purchase: produce(state.purchase, (draft) => {
                    const activeItem = draft.items[itemIndex];
                    const existingLineItems = activeItem.equipmentLineItems ?? [];

                    const existing = existingLineItems.find((lineItem) => lineItem.template.id === templateId);
                    if (existing) {
                        existing.quantity = newQuantity;
                    } else if (newQuantity > 0) {
                        existingLineItems.push({
                            type: LineItemType.EQUIPMENT,
                            quantity: newQuantity,
                            template: { id: templateId },
                            name,
                            sequence,
                            includeInAmount: true,
                        });
                    }

                    activeItem.equipmentLineItems = existingLineItems.filter((lineItem) => lineItem.quantity > 0);
                    activeItem.quantity = sumBy(activeItem.equipmentLineItems, "quantity");
                }),
            }));
        },

        updateAttendeeQuantity(
            itemIndex: number,
            templateId: ID,
            newQuantity: number,
            name: string,
            sequence?: number,
        ) {
            set((state) => ({
                purchase: produce(state.purchase, (draft) => {
                    const activeItem = draft.items[itemIndex];
                    const existingLineItems = activeItem.attendeeLineItems ?? [];

                    const existing = existingLineItems.find((lineItem) => lineItem.template.id === templateId);
                    if (existing) {
                        existing.quantity = newQuantity;
                    } else if (newQuantity > 0) {
                        existingLineItems.push({
                            type: LineItemType.DEMOGRAPHIC_SECONDARY,
                            quantity: newQuantity,
                            template: { id: templateId },
                            name,
                            sequence,
                            includeInAmount: false,
                        });
                    }

                    activeItem.attendeeLineItems = existingLineItems.filter((lineItem) => lineItem.quantity > 0);
                }),
            }));
        },

        updateGuests(itemIndex: number, guests: Guest[]) {
            set((state) => ({
                purchase: produce(state.purchase, (draft) => {
                    draft.items[itemIndex].guests = guests;
                }),
            }));
        },
        setPayment(payment) {
            set((state) => {
                return produce(state, (draft) => {
                    draft.paymentLineItem.payment = payment;
                });
            });
        },

        setPaymentMethod(method) {
            set((state) => {
                return produce(state, (draft) => {
                    draft.paymentLineItem.payment.method = method;
                });
            });
        },

        initializePaymentLineItem(amount: number, paymentMethod: PaymentMethod) {
            set((state) => {
                return produce(state, (draft) => {
                    draft.paymentLineItem.amount = -1 * Math.abs(amount);
                    draft.paymentLineItem.payment.method = paymentMethod;
                });
            });
        },
    },
}));

const isSameEvent = (previousItem: CreatePurchaseItemState, item: CreatePurchaseItemState) => {
    return (
        previousItem.arrivalDate === item.arrivalDate &&
        previousItem.arrivalTime === item.arrivalTime &&
        previousItem.privacy === item.privacy &&
        previousItem.product.id === item.product.id
    );
};

/** Privacy counts as a move, since with multiple private events per slot a private booking gets its own event. */
const omitEventRestrictionErrorsForMovedItems = (
    byItem: Record<string, ItemRestrictionErrors>,
    previousItems: CreatePurchaseItemState[],
    items: CreatePurchaseItemState[],
) => {
    if (isEmpty(byItem)) {
        return byItem;
    }

    let result = byItem;
    for (const [index, item] of items.entries()) {
        const itemKey = getRestrictionErrorsKeyForItem(byItem, item.id, index);
        const previousItem = item.id ? previousItems.find(({ id }) => id === item.id) : previousItems[index];
        if (byItem[itemKey] && previousItem && !isSameEvent(previousItem, item)) {
            result = { ...result, [itemKey]: omitEventRestrictionErrors(byItem[itemKey]) };
        }
    }

    return result;
};

export const clearDraftPurchasesCache = () => {
    queryClient.removeQueries(["draft", "purchases"]);
};

export const hasZeroDiscountCoupon = (purchase: CreatePurchaseState) => {
    return purchase.items.some((item) => {
        return item.lineItems.some((lineItem) => {
            return "type" in lineItem && lineItem.type === LineItemType.COUPON_DISCOUNT && lineItem.amount === 0;
        });
    });
};

export const getExperienceAttendeeLineItems = (experience: Experience): AttendeeLineItem[] => {
    const secondaryItems = getCatalogItemsByUnitType(experience.catalog?.items ?? [], UnitType.DEMOGRAPHIC_SECONDARY);
    return sortBy(secondaryItems, "sequence").map((item, index) => ({
        type: LineItemType.DEMOGRAPHIC_SECONDARY,
        quantity: index === 0 ? 1 : 0,
        template: { id: item.template?.id ?? "" },
        name: item.name,
        sequence: item.sequence,
        includeInAmount: false,
    }));
};

export const getExperienceEquipmentLineItems = (experience: Experience, timeslot?: Timeslot): EquipmentLineItem[] => {
    const equipmentItems = sortBy(
        getCatalogItemsByUnitType(experience.catalog?.items ?? [], UnitType.EQUIPMENT),
        "sequence",
    );

    // Default a quantity of 1 to the first AVAILABLE equipment (timeslot unit with count > 0), not blindly to the
    // first one — otherwise an unavailable first equipment starts at 1 and can't be reduced to 0 to proceed.
    // Without a timeslot (e.g. gift flow) availability is unknown, so fall back to defaulting the first item.
    const isAvailable = (templateId?: string) => {
        if (!timeslot) {
            return true;
        }
        const unit = timeslot.units?.find((u) => u.template?.id === templateId);
        // No matching timeslot unit means availability isn't constrained here; treat as available.
        return unit === undefined || (unit.count ?? 0) > 0;
    };
    const defaultIndex = equipmentItems.findIndex((item) => isAvailable(item.template?.id));

    return equipmentItems.map((item, index) => ({
        type: LineItemType.EQUIPMENT,
        quantity: index === defaultIndex ? 1 : 0,
        template: { id: item.template?.id ?? "" },
        name: item.name,
        sequence: item.sequence,
        includeInAmount: true,
    }));
};

export const getExperienceDemographicLineItems = (demographics: ProductCatalogItem[], min: number | null) => {
    const demographicLineItems = sortBy(demographics, "sequence").map((demographic, index) => ({
        type: LineItemType.DEMOGRAPHIC,
        quantity: index === 0 ? min ?? 0 : 0,
        template: { id: demographic.template?.id },
    }));

    return demographicLineItems as DemographicLineItem[];
};

export const getExperienceAddonLineItems = (experience: Experience) => {
    const experienceAddons = experience ? getAddOns(experience) : [];

    const choicesWithDefaults = experienceAddons.filter(
        (addon) => addon.items && addon.defaultChoice && getDefaultChoiceTemplateId(addon),
    );

    const addonLineItems = sortBy(choicesWithDefaults, "sequence").map((addon) => {
        const templateId = getDefaultChoiceTemplateId(addon);
        const type = addon.unitType === UnitType.PICKUP ? LineItemType.PICKUP : LineItemType.ADDON;

        return {
            type,
            quantity: 1,
            template: { id: templateId },
            catalog: { template: templateId },
        };
    });

    return addonLineItems as CatalogLineItem[];
};

export const getMerchandisePurchaseWareLineItems = (merchandise: Merchandise) => {
    const availableCatalogItems = filterPurchasableMerchandiseCatalogItems(merchandise);
    const hasTrackedInventory = isInventoryTracked(merchandise);

    if (availableCatalogItems.length === 0) {
        return [];
    }

    const sorted = sortBy(availableCatalogItems, "sequence");

    // Find the first catalog item that can be selected in purchase flow.
    const firstPurchasableIndex = sorted.findIndex((item) => {
        const hasPrice = hasCatalogItemPrice(item);
        if (!hasTrackedInventory) {
            return hasPrice;
        }

        return hasPrice && getMerchandiseVariantMaxSelectableQuantity(item, hasTrackedInventory) > 0;
    });

    // Create ware line items for all available catalog items with templates
    const wareLineItems = sorted.map((catalogItem, index) => ({
        type: LineItemType.WARE,
        quantity: index === firstPurchasableIndex ? 1 : 0,
        template: { id: catalogItem.template!.id },
        includeInAmount: true,
    }));

    return wareLineItems as WareLineItem[];
};

export const getItemProduct = <T extends CreatePurchaseProduct = CreatePurchaseProduct>(
    item: CreatePurchaseItemState,
) => {
    // We will always have the experience in the cache before this is called
    return queryClient.getQueryData<T>(["products", item.product.id])!;
};

export const getPurchasePackageDetails = (item: CreatePurchaseItemState) => {
    // We will always have the Product in the cache before this is called
    return queryClient.getQueryData<Package | PartnerPackage>(["products", item?.parentProduct?.id])!;
};

export enum CreatePurchaseStep {
    /** When user is doing MIST checkout for an experience / gift offer product, this would be the first screen (Selecting timeslot, add-ons, questionnaire, recipient details, etc.)  */
    SELECT_DATE = "selectDate",

    /**
     * Same as `SELECT_DATE` for gift offer with a difference of : When the screen is rendered, it would auto validate the gift values form and displays errors if any
     *
     * Use cases:
     *
     * 1. For a MIST scenario, where user have 2 products in the cart, One is a regular experience and second is a gift product.
     *  Now, suppose user has not entered some required fields for gift offer and currently the active item is experience product with all the valid inputs.
     *  At this moment, we would allow him to go to `CHECKOUT` step, as all the validations for active item (experience product) are passing.
     *  But, we don't want to confirm the purchase until user enters valid details for gift offer also.
     *
     *  For this, when user clicks "Pay" button, we check for any invalid gift products in MIST cart, and if any product like that exists,
     *  we change the active item to that invalid product and display all the error messages.
     *
     *  So, after validation on the `CHECKOUT` step, rather than sending user on `SELECT_DATE` page, we send him to `GIFT_DETAILS_INVALID`, and validate the form on initial render
     **/
    GIFT_DETAILS_INVALID = "giftDetailsInvalid",

    /** Main Checkout screen where user enters all the payment details. For Single-step and package checkout user would see only this step */
    CHECKOUT = "checkout",

    WAITLIST_SUCCESS = "waitlist_success",
}

export const isNowPaymentWorkflowPurchase = (paymentLineItem: PaymentLineItem, purchase: CreatePurchaseState) => {
    if (
        // Payment Workflow: Later
        paymentLineItem.payment.method === PaymentMethod.LATER ||
        // Payment Workflow: Deposit
        purchase.paymentReminders?.length ||
        // Payment Workflow: Split Payment
        purchase.splitPayment.enabled
    ) {
        return false;
    }

    return true;
};
